import axios, { AxiosInstance } from 'axios';

// Get the locale from localStorage, cookies, or default to 'en'
const getLocale = () => {
  return localStorage.getItem('lang') || 'en';
};

const getTimezone = () => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return userTimeZone;
};

// Shared configuration + request interceptor (XSRF header, locale, timezone).
// Both instances below are built from this, so they authenticate identically.
const createInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    withCredentials: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
  });

  instance.interceptors.request.use(
    (config) => {
      const xsrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (xsrfToken) {
        config.headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
      }
      config.headers['Accept-Language'] = getLocale();
      config.headers['Timezone'] = getTimezone();
      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
};

// The main instance. When a request comes back unauthenticated, it treats the
// session as dead and sends the user to the login/404 screen. Correct for real
// user actions, where a lost session should indeed bounce them out.
const instance = createInstance();

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      window.location.href = '/app/404';
    }
    if (
      error.response &&
      error.response.status === 500 &&
      error.response?.data?.error?.message === 'Unauthenticated.'
    ) {
      window.location.href = '/app/404';
    }
    return Promise.reject(error);
  }
);

// A second instance with the SAME auth/cookie behaviour but NO redirect on
// failure. Use it for background calls that must never log the user out if they
// fail -- e.g. minting the sales-bot token. If such a call fails, the caller
// handles it quietly and the app carries on. See ProfilePage's token handshake.
export const apiQuiet = createInstance();

export default instance;
